import React, { useEffect, useRef, useState } from "react";
import { FormField, Button, Icon, Image, ImageGroup, Modal } from "@/components/bp/bp-ui";
import ImageModal, { IUploadedImage } from "./ImageModal";
import { BPButton } from "@/components/bp/bpbutton";

// export interface IUploadedImage {
//     id: number;
//     fileName: string;
//     mimeType: string;
//     size: number;
//     base64DataUrl: string; // base64
//     file: File;
// }

interface IImageMultiUploadProps {
    name: string;
    label?: string;
    error?: string;
    placeholderText?: string;
    imagesUploaded: (name: string, data: IUploadedImage[]) => void;
    value?: IUploadedImage[];
    required?: boolean;

    // New prop: if true (default) clicking a thumbnail opens a modal dialog to view the image.
    showInDialogOnClick?: boolean;
    enableKeyboardNavigation?: boolean;
    enableZoomPan?: boolean;
    showMetadata?: boolean;
    disabled?: boolean;
}

export const ImageMultiUpload: React.FC<IImageMultiUploadProps> = ({
    name,
    label,
    error,
    placeholderText,
    imagesUploaded,
    value = [],
    required,
    showInDialogOnClick = true,
    enableKeyboardNavigation = true,
    enableZoomPan = true,
    showMetadata = true,
    disabled = false
}) => {
    const [images, setImages] = useState<IUploadedImage[]>(value);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalImageIndex, setModalImageIndex] = useState<number | null>(null);

    useEffect(() => {
        setImages(value);
    }, [value]);

    const handleFiles = (files: FileList | null) => {
        if (!files) return;
        const fileArr = Array.from(files);
        const imagePromises = fileArr.map(file => {
            return new Promise<IUploadedImage>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = e => {
                    const dataUrl = e.target?.result as string;
                    // const dataUrlBase64 = dataUrl.split(',')[1];
                    resolve({
                        id: null,
                        fileName: file.name,
                        mimeType: file.type,
                        size: file.size,
                        base64DataUrl: dataUrl,
                        file
                    } as unknown as IUploadedImage);
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        });

        Promise.all(imagePromises).then(newImages => {
            const allImages = [...images, ...newImages];
            setImages(allImages);
            imagesUploaded(name, allImages);
        });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFiles(e.target.files);
        // Reset input so same file can be selected again
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleRemove = (idx: number) => {
        const newImages = images.filter((_, i) => i !== idx);
        setImages(newImages);
        imagesUploaded(name, newImages);
        // if currently showing this image in modal, close modal
        if (modalImageIndex === idx) {
            setModalOpen(false);
            setModalImageIndex(null);
        } else if (modalImageIndex !== null && modalImageIndex > idx) {
            // adjust modal index if removing a preceding image
            setModalImageIndex(modalImageIndex - 1);
        }
    };

    const openModalForIndex = (idx: number) => {
        if (!showInDialogOnClick) return;
        setModalImageIndex(idx);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setModalImageIndex(null);
    };

    return (
        <FormField name={name} error={!!error} required={required}>
            {label && <label>{label}</label>}
            <div className="ui action input">
                <input
                    type="text"
                    placeholder={placeholderText}
                    value={images.map(img => img.fileName).join(", ")}
                    readOnly
                    style={{ cursor: "pointer" }}
                    disabled={disabled}
                    onClick={() => fileInputRef.current?.click()}
                />
                <input
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={disabled}
                    style={{ display: "none" }}
                    ref={fileInputRef}
                    onChange={handleInputChange}
                />
                <BPButton disabled={disabled} icon onClick={() => fileInputRef.current?.click()} type="button">
                    <Icon name="attach" />
                </BPButton>
            </div>
            {error && <div className="ui pointing red basic label">{error}</div>}
            <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 10 }}>
                <ImageGroup size="small">
                    {images.map((img, idx) => (
                        <div key={idx} style={{ position: "relative", display: "inline-block" }}>
                            <Image
                                src={img.base64DataUrl}
                                bordered
                                wrapped
                                spaced
                                style={{ cursor: showInDialogOnClick ? "pointer" : undefined }}
                                onClick={() => openModalForIndex(idx)}
                            />
                            <BPButton
                                icon="close"
                                size="mini"
                                color="red"
                                style={{ position: "absolute", top: 2, right: 2 }}
                                onClick={() => handleRemove(idx)}
                                type="button"
                                disabled={disabled}
                            />
                        </div>
                    ))}
                </ImageGroup>
            </div>

            <ImageModal
                images={images}
                open={modalOpen}
                initialIndex={modalImageIndex}
                onClose={closeModal}
                enableKeyboardNavigation={enableKeyboardNavigation}
                enableZoomPan={enableZoomPan}
                showMetadata={showMetadata}
            />
        </FormField>
    );
};